import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import {
  BUILDING_INFO,
  productionCatalogFor,
  PRODUCTION_BUILDING_FAMILIES,
  PRODUCTION_ITEM_BY_KEY,
  queueCapacity,
  workshopSlotCount,
  type ProductionBuildingFamily,
  type ProductionItem,
} from "@stahlfront/core";
import type { ProductionJobDto } from "@stahlfront/shared";
import {
  claimProduction,
  fetchProductionList,
  getMe,
  startProduction,
} from "../api.js";
import { useStore } from "../store.js";

/** §6.2 production interface — opened from the action ring "Open Production" verb. */

const POLL_MS = 1000;
/** Update timer text at this cadence; the bar itself animates via DOM ref. */
const TIMER_REFRESH_MS = 200;

export function ProductionPanel(): JSX.Element | null {
  const open = useStore((s) => s.productionPanelOpen);
  const buildingId = useStore((s) => s.productionPanelBuildingId);
  const buildings = useStore((s) => s.buildings);
  const setOpen = useStore((s) => s.setProductionPanelOpen);
  const setBuildingId = useStore((s) => s.setProductionPanelBuildingId);
  const me = useStore((s) => s.me);
  const setMe = useStore((s) => s.setMe);
  const productionJobs = useStore((s) => s.productionJobs);
  const setProductionJobs = useStore((s) => s.setProductionJobs);
  const setSpeedupConfirm = useStore((s) => s.setSpeedupConfirm);

  const [serverNowOffsetMs, setServerNowOffsetMs] = useState(0);
  const [actionPending, setActionPending] = useState(false);
  const [claimingJobId, setClaimingJobId] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const baseId = me?.base.id ?? "";

  const buildingJobs = useMemo(
    () => productionJobs.filter((j) => j.baseBuildingId === buildingId),
    [productionJobs, buildingId],
  );

  const refresh = useCallback(async (): Promise<void> => {
    if (!baseId) return;
    try {
      const res = await fetchProductionList(baseId);
      setProductionJobs(res.jobs);
      setServerNowOffsetMs(res.serverNowMs - Date.now());
      setLoaded(true);
    } catch (err) {
      console.warn("[stahlfront] production list refresh failed:", err);
    }
  }, [baseId, setProductionJobs]);

  useEffect(() => {
    if (!open) return;
    setLoaded(false);
    void refresh();
    const id = setInterval(() => void refresh(), POLL_MS);
    const onComplete = (): void => {
      void refresh();
    };
    window.addEventListener("stahlfront:production.complete", onComplete);
    return () => {
      clearInterval(id);
      window.removeEventListener("stahlfront:production.complete", onComplete);
    };
  }, [open, refresh]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  const close = (): void => {
    setOpen(false);
    setBuildingId(null);
    setErrorText(null);
  };

  if (!open || !buildingId) return null;

  const building = buildings.find((b) => b.id === buildingId);
  if (!building) return null;
  if (
    !(PRODUCTION_BUILDING_FAMILIES as readonly string[]).includes(building.family as string)
  ) {
    return null;
  }
  const family = building.family as ProductionBuildingFamily;
  const meta = BUILDING_INFO[family];
  const accent = `oklch(58% 0.16 ${meta.accentHue})`;
  const slotCap = workshopSlotCount(Math.max(1, building.level));
  const queueCap = queueCapacity(Math.max(1, building.level));
  const catalog = productionCatalogFor(family);

  const active = buildingJobs.filter((j) => j.state === "active" || j.state === "complete");
  const queued = buildingJobs
    .filter((j) => j.state === "queued")
    .sort((a, b) => a.enqueuedAtMs - b.enqueuedAtMs);
  const totalPending = active.length + queued.length;

  const slotAssignment: (ProductionJobDto | null)[] = Array.from({ length: slotCap }, (_, i) => {
    return active.find((j) => j.slotIndex === i) ?? null;
  });

  const onStart = async (item: ProductionItem): Promise<void> => {
    if (actionPending || totalPending >= slotCap + queueCap) return;
    setActionPending(true);
    setErrorText(null);
    try {
      await startProduction({ baseBuildingId: building.id, itemKey: item.key });
      await refresh();
      // Wallet drained — refresh top HUD.
      const fresh = await getMe();
      if (fresh) setMe(fresh);
    } catch (err) {
      const m = String(err);
      if (m.includes("INSUFFICIENT_TREASURY")) setErrorText("Treasury too low — check the wallet.");
      else if (m.includes("INSUFFICIENT_RESERVES")) setErrorText("Reserves too low.");
      else if (m.includes("QUEUE_FULL")) setErrorText("Slots and queue are full.");
      else setErrorText("Couldn't start production. Try again.");
    } finally {
      setActionPending(false);
    }
  };

  /**
   * Phase 2.D — open the shared Speed-Up confirm modal. The modal owns the
   * diamond cost preview, the live ETA and the API call; this panel just
   * hands it the jobId and stays out of the way.
   */
  const onSpeedup = (jobId: string): void => {
    setSpeedupConfirm({ kind: "production", jobId });
  };

  const onClaim = async (jobId: string): Promise<void> => {
    if (claimingJobId) return;
    setClaimingJobId(jobId);
    setErrorText(null);
    try {
      const res = await claimProduction({ jobId });
      // Apply wallet snapshot from response (avoids second round-trip).
      if (me) {
        setMe({ ...me, wallet: res.wallet });
      } else {
        const fresh = await getMe();
        if (fresh) setMe(fresh);
      }
      await refresh();
    } catch (err) {
      console.warn("[stahlfront] claim failed:", err);
      setErrorText("Couldn't claim — try again.");
    } finally {
      setClaimingJobId(null);
    }
  };

  const onFill = async (item: ProductionItem): Promise<void> => {
    if (actionPending) return;
    const remaining = Math.max(0, slotCap + queueCap - totalPending);
    if (remaining === 0) return;
    setActionPending(true);
    setErrorText(null);
    try {
      for (let i = 0; i < remaining; i++) {
        try {
          await startProduction({ baseBuildingId: building.id, itemKey: item.key });
        } catch (err) {
          if (String(err).includes("QUEUE_FULL")) break;
          if (String(err).includes("INSUFFICIENT_TREASURY")) {
            setErrorText("Treasury exhausted partway through Fill.");
            break;
          }
          if (String(err).includes("INSUFFICIENT_RESERVES")) {
            setErrorText("Reserves exhausted partway through Fill.");
            break;
          }
          throw err;
        }
      }
      await refresh();
      const fresh = await getMe();
      if (fresh) setMe(fresh);
    } catch (err) {
      console.warn("[stahlfront] fill queue failed:", err);
      setErrorText("Couldn't fill the queue. Try again.");
    } finally {
      setActionPending(false);
    }
  };

  return (
    <div className="prod-backdrop" onPointerDown={close} role="dialog" aria-modal="true">
      <div
        className="prod-panel"
        onPointerDown={(e) => e.stopPropagation()}
        style={{ ["--prod-accent" as string]: accent }}
      >
        <header className="prod-titlebar">
          <span className="prod-title-name">{meta.displayName}</span>
          <span className="prod-title-lv">Lv {building.level}</span>
          <span className="prod-title-cap" aria-label="Slots and queue used">
            {totalPending} / {slotCap + queueCap}
          </span>
          <button type="button" className="prod-close" aria-label="Close production panel" onClick={close}>
            ×
          </button>
        </header>

        {errorText ? (
          <p className="prod-error" role="alert">
            {errorText}
          </p>
        ) : null}

        {!loaded ? (
          <div className="prod-loading" aria-live="polite">
            <div className="prod-skeleton-bar" />
            <div className="prod-skeleton-bar" />
            <div className="prod-skeleton-bar" />
          </div>
        ) : (
          <>
            <section className="prod-slots" aria-label="Workshop slots">
              {slotAssignment.map((job, i) => (
                <SlotTile
                  key={i}
                  index={i}
                  job={job}
                  serverNowOffsetMs={serverNowOffsetMs}
                  onSpeedup={onSpeedup}
                  onClaim={onClaim}
                  actionPending={actionPending}
                  claimingJobId={claimingJobId}
                />
              ))}
            </section>

            <section className="prod-queue" aria-label="Queue strip">
              <div className="prod-queue-header">
                <span className="prod-queue-label">Queue</span>
                <span className="prod-queue-count">
                  {queued.length} / {queueCap}
                </span>
              </div>
              <div className="prod-queue-list">
                {queued.length === 0 ? (
                  <span className="prod-queue-empty">Queue is empty — start a job above</span>
                ) : (
                  queued.map((j) => {
                    const item = PRODUCTION_ITEM_BY_KEY[j.itemKey];
                    const name = item?.displayName ?? j.itemKey;
                    return (
                      <span
                        key={j.id}
                        className="prod-queue-chip"
                        title={`${name} — queued`}
                        aria-label={`${name} queued`}
                      >
                        {name}
                      </span>
                    );
                  })
                )}
              </div>
            </section>

            <section className="prod-catalog" aria-label="Producible items">
              {catalog.map((item) => (
                <CatalogRow
                  key={item.key}
                  item={item}
                  disabled={actionPending || totalPending >= slotCap + queueCap}
                  onStart={() => void onStart(item)}
                  onFill={() => void onFill(item)}
                />
              ))}
            </section>
          </>
        )}

        <footer className="prod-footer">
          <span className="prod-footer-meta">
            Workshop production · {slotCap} slot{slotCap === 1 ? "" : "s"} ·
            {" "}
            {queueCap} queue
          </span>
        </footer>
      </div>
    </div>
  );
}

interface SlotTileProps {
  readonly index: number;
  readonly job: ProductionJobDto | null;
  readonly serverNowOffsetMs: number;
  readonly actionPending: boolean;
  readonly claimingJobId: string | null;
  readonly onSpeedup: (jobId: string) => void;
  readonly onClaim: (jobId: string) => Promise<void>;
}
/**
 * Slot tile drives the progress bar via direct DOM ref mutation per rAF —
 * no React re-render on every frame. Timer text refreshes at TIMER_REFRESH_MS
 * cadence (5 Hz) which is plenty for legibility without thrash.
 *
 * When the job's state flips to `complete`, the tile gains a `.prod-slot-ready`
 * class that triggers a 320ms gold pulse + replaces Speed-Up with Claim.
 */
function SlotTile({
  index,
  job,
  serverNowOffsetMs,
  actionPending,
  claimingJobId,
  onSpeedup,
  onClaim,
}: SlotTileProps): JSX.Element {
  const fillRef = useRef<HTMLDivElement | null>(null);
  const [remainingLabel, setRemainingLabel] = useState<string>("");

  useEffect(() => {
    if (!job) return;
    const startMs = job.startedAtMs;
    const totalMs = job.durationMs;
    let raf = 0;
    let lastTextUpdate = 0;
    const tick = (): void => {
      const nowMs = Date.now() + serverNowOffsetMs;
      const elapsed = Math.max(0, nowMs - startMs);
      const ratio = totalMs > 0 ? Math.min(1, elapsed / totalMs) : 1;
      const fill = fillRef.current;
      if (fill) fill.style.width = `${(ratio * 100).toFixed(2)}%`;
      if (nowMs - lastTextUpdate >= TIMER_REFRESH_MS) {
        lastTextUpdate = nowMs;
        setRemainingLabel(formatDuration(Math.max(0, totalMs - elapsed)));
      }
      // Stop the rAF when the job is fully visualised at 100% AND server-side
      // state == complete; further frames would just re-paint the same width.
      if (ratio >= 1 && job.state === "complete") {
        if (fill) fill.style.width = "100%";
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [job, serverNowOffsetMs]);

  if (!job) {
    return (
      <div className="prod-slot prod-slot-empty" aria-label={`Workshop slot ${index + 1} — empty`}>
        <span className="prod-slot-empty-tag">Slot {index + 1}</span>
        <span className="prod-slot-empty-msg">Idle — start a job below</span>
      </div>
    );
  }

  const item = PRODUCTION_ITEM_BY_KEY[job.itemKey];
  const itemName = item?.displayName ?? job.itemKey;
  const isComplete = job.state === "complete";
  const isClaiming = claimingJobId === job.id;

  return (
    <div
      className={["prod-slot", isComplete ? "prod-slot-ready" : ""].filter(Boolean).join(" ")}
      aria-label={`Workshop slot ${index + 1}: ${itemName}${isComplete ? " (ready to claim)" : ""}`}
    >
      <span className="prod-slot-index">Slot {index + 1}</span>
      <span className="prod-slot-name">{itemName}</span>
      <div className="prod-progress" aria-hidden="true">
        <div className="prod-progress-fill" ref={fillRef} />
      </div>
      <span className="prod-slot-time" aria-live="polite">
        {isComplete ? "Ready" : remainingLabel}
      </span>
      {isComplete ? (
        <button
          type="button"
          className="prod-slot-claim"
          onClick={() => void onClaim(job.id)}
          disabled={isClaiming || actionPending}
          aria-busy={isClaiming}
          aria-label="Claim completed job"
          title="Bank the produced item into your wallet / inventory"
        >
          {isClaiming ? (
            <>
              <span className="prod-spinner" aria-hidden="true" /> Claiming
            </>
          ) : (
            "Claim"
          )}
        </button>
      ) : (
        <button
          type="button"
          className="prod-slot-speedup"
          onClick={() => onSpeedup(job.id)}
          disabled={actionPending}
          aria-label="Speed up this job"
          title="Spend diamonds to finish this job now"
        >
          Speed-Up
        </button>
      )}
    </div>
  );
}

interface CatalogRowProps {
  readonly item: ProductionItem;
  readonly disabled: boolean;
  readonly onStart: () => void;
  readonly onFill: () => void;
}
function CatalogRow({ item, disabled, onStart, onFill }: CatalogRowProps): JSX.Element {
  return (
    <div className="prod-catalog-row" aria-label={item.displayName}>
      <div className="prod-catalog-info">
        <span className="prod-catalog-name">{item.displayName}</span>
        <span className="prod-catalog-summary">{item.summary}</span>
        <span className="prod-catalog-meta">
          Base {formatDuration(item.baseDurationMs)} · Acceler {formatDuration(item.accelDurationMs)} ·
          {" "}
          {item.costTreasury}t{item.costReserves > 0 ? ` + ${item.costReserves}r` : ""}
        </span>
      </div>
      <div className="prod-catalog-actions">
        <button
          type="button"
          className="prod-btn prod-btn-primary"
          disabled={disabled}
          onClick={onStart}
        >
          Start
        </button>
        <button
          type="button"
          className="prod-btn prod-btn-secondary"
          disabled={disabled}
          onClick={onFill}
          title="Start jobs until the queue is full"
        >
          Fill Queue
        </button>
      </div>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms <= 0) return "0s";
  const sec = Math.ceil(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rsec = sec % 60;
  if (min < 60) return `${min}m ${rsec.toString().padStart(2, "0")}s`;
  const h = Math.floor(min / 60);
  const rmin = min % 60;
  return `${h}h ${rmin.toString().padStart(2, "0")}m`;
}
